import { PATH_AUTH, PATH_PAGE } from '../../../routes/paths';

/**
 * Build le config de navigation à partir du contexte courant.
 *
 * `hasPermission` est passé en argument explicite par l'appelant (souvent
 * via `useHasPermission()` côté React) pour rendre la dépendance visible
 * — auparavant on l'appelait via `useAdminStore.getState()` ici-même, ce
 * qui empêchait toute réactivité côté composants consommateurs
 * (CW-21903).
 */
export default {
  get: (env, user, features, translate, hasPermission) => {
    // Si l'utilisateur n'est pas connecté, on ne retourne rien
    if (!user) {
      return [];
    }

    const isMobile = window.env.isApp || window.env.isMobileDevice;
    // Fallback défensif : si `hasPermission` n'est pas fourni (appelant
    // historique ou test unitaire qui n'a pas le store), on considère
    // qu'aucune permission n'est accordée — choix sûr par défaut.
    const can = typeof hasPermission === 'function' ? hasPermission : () => false;
    const canModifyForm = can('form_control');
    const canManagePatrols = can('patrol_control');
    const canCreateEvent = can('event_create');
    const canManageEvents = can('mce_read');

    // Manager
    const itemsManager = [
      {
        title: 'Tableau de bord',
        path: PATH_PAGE.manager.root,
        id: 'dashboard-list-nav',
        icon: 'dashboard'
      }
    ];

    if (env.settings.man_access_list_resource) {
      itemsManager.push({
        title: 'Agents',
        path: PATH_PAGE.general.resource.resources,
        id: 'resources-list-nav',
        icon: 'group'
      });
    }
    // Administration section (manager admin link)
    const itemsAdmin = [
      {
        title: 'Utilisateurs',
        path: PATH_PAGE.admin.users,
        id: 'users-list-nav',
        icon: 'group'
      },
      {
        title: 'Droits d’accès',
        path: PATH_PAGE.admin.permissions,
        id: 'admin-permissions-nav',
        icon: 'visibility_lock'
      }
    ];

    if (env.settings.man_access_list_site) {
      itemsManager.push({
        title: 'Sites',
        path: PATH_PAGE.general.site.siteList,
        id: 'sites-list-nav',
        icon: 'site'
      });
    }

    if (env.settings.man_access_services) {
      itemsManager.push({
        title: 'Pointages',
        path: PATH_PAGE.general.services,
        id: 'services-list-nav',
        icon: 'punch_clock'
      });
    }

    // MCE Menu (version >= 2.0.0) — mobile uniquement dans itemsManager
    if (features?.mce && isMobile) {
      const mceManagerMenuItems = [];

      if (canManageEvents && !user.userResourceId) {
        mceManagerMenuItems.push({
          title: translate('mce:navigation_events_title'),
          icon: 'menu_book',
          path: PATH_PAGE.manager.mce.events
        });
      }

      if (canModifyForm) {
        mceManagerMenuItems.push({
          title: translate('mce:navigation_forms_title'),
          icon: 'form_edit',
          path: PATH_PAGE.manager.mce.root
        });
      }

      if (canManagePatrols) {
        mceManagerMenuItems.push({
          title: translate('mce:navigation_patrols_title'),
          icon: 'patrol',
          path: PATH_PAGE.manager.mce.patrols
        });
      }

      if (canCreateEvent) {
        mceManagerMenuItems.push({
          title: translate('mce:navigation_title_resource'),
          icon: 'campaign',
          path: PATH_PAGE.resource.mce.root
        });
      }

      if (mceManagerMenuItems.length > 0) {
        itemsManager.push({
          title: translate('mce:mce_forms'),
          path: mceManagerMenuItems[0].path,
          icon: 'form_edit',
          mobileTitle: translate('mce:navigation_mobile_title'),
          mobileIcon: 'settings',
          id: 'mce-menu-nav',
          menuItems: mceManagerMenuItems
        });
      }
    }

    // Admin Menu (version >= 2.0.0) — mobile uniquement dans navConfig et admin link
    if (isMobile && user.is_admin_link) {
      itemsManager.push({
        title: 'Admin',
        path: PATH_PAGE.admin.root,
        icon: 'admin_panel_settings',
        id: 'admin-menu-nav',
        menuItems: itemsAdmin
      });
    }

    // Ressource
    const itemsResource = [];
    const hasMcePermissions = canModifyForm || canCreateEvent || canManageEvents;
    const hasMoreMenu = isMobile && (user.is_admin_link || hasMcePermissions);

    // Évènements en standalone dans la bottom bar mobile (quand pas de menu "Plus")
    if (canCreateEvent && isMobile && !hasMoreMenu) {
      itemsResource.push({
        title: translate('mce:navigation_title_resource'),
        path: PATH_PAGE.resource.mce.root,
        icon: 'campaign',
        id: 'events-nav-resource'
      });
    }

    if (user.hasOneContractActive) {
      itemsResource.push({
        title: 'Tableau de bord',
        id: 'dashboard-list-nav',
        path: PATH_PAGE.resource.root,
        icon: 'dashboard'
      });
    }

    if (user.hasOneContractActive) {
      itemsResource.push({
        title: 'Ma fiche',
        id: 'sheet-nav',
        path: PATH_PAGE.resource.sheet,
        icon: 'assignment'
      });
    }

    if (env.settings.res_access_schedule && user.hasOneContractActive) {
      itemsResource.push({
        title: 'Planning',
        id: 'planning-nav',
        path: PATH_PAGE.resource.schedule,
        icon: 'calendar_month'
      });
    }

    if (env.settings.res_access_demands && user.hasOneContractActive) {
      itemsResource.push({
        title: 'Demandes',
        id: 'demands-nav',
        path: PATH_PAGE.resource.demands,
        icon: 'forum'
      });
    }

    if (features?.notifMission && env.settings.is_mission_enabled) {
      itemsResource.push({
        title: 'Missions',
        id: 'missions-nav',
        path: PATH_PAGE.resource.missions,
        icon: 'star'
      });
    }

    // Menu "Plus" mobile pour Agent (regroupe Évènements + Formulaires + Admin)
    if (isMobile && hasMoreMenu) {
      const moreMenuItems = [];

      if (canCreateEvent) {
        moreMenuItems.push({
          title: translate('mce:navigation_title_resource'),
          icon: 'campaign',
          path: PATH_PAGE.resource.mce.root
        });
      }

      if (canManageEvents && !user.userResourceId) {
        moreMenuItems.push({
          title: translate('mce:navigation_events_title'),
          icon: 'form_edit',
          path: PATH_PAGE.manager.mce.events
        });
      }

      if (canModifyForm) {
        moreMenuItems.push({
          title: translate('mce:mce_forms'),
          icon: 'form_edit',
          path: PATH_PAGE.manager.mce.root
        });
      }

      if (user.is_admin_link) {
        itemsAdmin.forEach((item) => {
          moreMenuItems.push({ title: item.title, icon: item.icon, path: item.path });
        });
      }

      if (moreMenuItems.length > 0) {
        itemsResource.push({
          title: 'Plus',
          path: moreMenuItems[0].path,
          icon: 'more_horiz',
          id: 'more-menu-nav',
          menuItems: moreMenuItems
        });
      }
    }

    // Client
    const itemsClient = [
      {
        title: 'Tableau de bord',
        path: PATH_PAGE.client.root,
        icon: 'dashboard',
        id: 'dashboard-list-nav'
      }
    ];

    if (env.settings.cli_access_sites && !user?.forbiddenPages?.includes('VOSINF')) {
      const navCliSites = {
        title: 'Sites',
        path: PATH_PAGE.general.site.siteSheet,
        icon: 'site',
        id: 'sites-list-nav'
      };
      itemsClient.push(navCliSites);
    }

    if (env.settings.cli_access_quotes && !user?.forbiddenPages?.includes('DEVIS')) {
      itemsClient.push({
        title: 'Devis',
        path: PATH_PAGE.client.quotes,
        icon: 'order_approve',
        id: 'quotes-list-nav'
      });
    }

    if (env.settings.cli_access_bills && !user?.forbiddenPages?.includes('FACTURES')) {
      itemsClient.push({
        title: 'Factures',
        path: PATH_PAGE.client.invoices,
        icon: 'receipt_long',
        id: 'invoices-list-nav'
      });
    }
    // On doit toujours aller sur PROVIDERS puis rediriger vers PROVIDER
    if (env.settings.cli_access_provider && !user?.forbiddenPages?.includes('NOSINF')) {
      itemsClient.push({
        title: 'Prestataire',
        path: PATH_PAGE.client.providers,
        icon: 'domain',
        id: 'provider-list-nav'
      });
    }

    // Menu "Plus" mobile pour Client (regroupe Formulaires + Admin)
    if (isMobile && (user.is_admin_link || canModifyForm || canCreateEvent || canManageEvents)) {
      const clientMoreItems = [];

      if (canCreateEvent) {
        clientMoreItems.push({
          title: translate('mce:navigation_title_resource'),
          icon: 'form_edit',
          path: PATH_PAGE.resource.mce.root
        });
      }

      if (canManageEvents) {
        clientMoreItems.push({
          title: translate('mce:navigation_events_title'),
          icon: 'form_edit',
          path: PATH_PAGE.manager.mce.events
        });
      }

      if (canModifyForm) {
        clientMoreItems.push({
          title: translate('mce:mce_forms'),
          icon: 'form_edit',
          path: PATH_PAGE.manager.mce.root
        });
      }

      if (user.is_admin_link) {
        itemsAdmin.forEach((item) => {
          clientMoreItems.push({ title: item.title, icon: item.icon, path: item.path });
        });
      }

      if (clientMoreItems.length > 0) {
        itemsClient.push({
          title: 'Plus',
          path: clientMoreItems[0].path,
          icon: 'more_horiz',
          id: 'more-menu-nav',
          menuItems: clientMoreItems
        });
      }
    }

    // Partner
    const itemsPartner = [
      {
        title: 'Tableau de bord',
        path: PATH_PAGE.manager_partner.root,
        icon: 'dashboard',
        id: 'dashboard-list-nav'
      }
    ];

    if (env.settings?.partner_access_schedules && !user?.forbiddenPages?.includes('PART_PLANNING')) {
      itemsPartner.push({
        title: 'Plannings',
        path: `${PATH_PAGE.general.site.siteSchedule}?siteId=0`,
        icon: 'planning',
        id: 'planning-nav'
      });
    }

    if (env.settings?.partner_access_resources && !user?.forbiddenPages?.includes('PART_AGENTS')) {
      itemsPartner.push({
        title: 'Agents',
        path: PATH_PAGE.general.resource.resources,
        icon: 'group',
        id: 'agents-nav'
      });
    }

    if (env.settings?.partner_access_commands && !user?.forbiddenPages?.includes('PART_BDC')) {
      itemsPartner.push({
        title: 'Commandes',
        path: PATH_PAGE.manager_partner.orders,
        icon: 'receipt_long',
        id: 'orders-nav'
      });
    }

    // MCE section desktop (section séparée avec subheader "MCE")
    const itemsMceDesktop = [];
    if (features?.mce && !isMobile) {
      if (canManageEvents && !user.userResourceId) {
        itemsMceDesktop.push({
          title: translate('mce:navigation_events_title'),
          path: PATH_PAGE.manager.mce.events,
          icon: 'menu_book',
          id: 'mce-events-nav'
        });
      }
      if (canModifyForm) {
        itemsMceDesktop.push({
          title: translate('mce:mce_forms'),
          path: PATH_PAGE.manager.mce.root,
          icon: 'form_edit',
          id: 'mce-forms-nav'
        });
      }
      if (canManagePatrols) {
        itemsMceDesktop.push({
          title: translate('mce:navigation_patrols_title'),
          path: PATH_PAGE.manager.mce.patrols,
          icon: 'patrol',
          id: 'mce-patrols-nav'
        });
      }
      if (canCreateEvent) {
        itemsMceDesktop.push({
          title: translate('mce:navigation_title_resource'),
          path: PATH_PAGE.resource.mce.root,
          icon: 'campaign',
          id: 'events-nav-resource'
        });
      }
    }

    // role
    const managerGroups = [{ subheader: 'Espace manager', items: itemsManager }];
    if (itemsMceDesktop.length > 0) {
      managerGroups.push({ subheader: 'MCE', items: itemsMceDesktop });
    }
    if (user.is_admin_link) {
      managerGroups.push({ subheader: 'Administration', items: itemsAdmin });
    }

    // Section MCE desktop pour agent (Évènements + Formulaires + Gestion évènements si permissions)
    const itemsMceResource = [];
    if (canCreateEvent) {
      itemsMceResource.push({
        title: translate('mce:navigation_title_resource'),
        path: PATH_PAGE.resource.mce.root,
        icon: 'campaign',
        id: 'events-nav-resource'
      });
    }
    if (canManageEvents && !user.userResourceId) {
      itemsMceResource.push({
        title: translate('mce:navigation_events_title'),
        path: PATH_PAGE.manager.mce.events,
        icon: 'form_edit',
        id: 'mce-events-nav'
      });
    }
    if (canModifyForm) {
      itemsMceResource.push({
        title: translate('mce:mce_forms'),
        path: PATH_PAGE.manager.mce.root,
        icon: 'form_edit',
        id: 'mce-forms-nav'
      });
    }

    const resourceGroups = [{ subheader: 'Espace agent', items: itemsResource }];
    if (itemsMceResource.length > 0 && !isMobile) {
      resourceGroups.push({ subheader: 'MCE', items: itemsMceResource });
    }
    if (user.is_admin_link) {
      resourceGroups.push({ subheader: 'Administration', items: itemsAdmin });
    }

    // Section MCE desktop pour client
    const itemsMceClient = [];
    if (canCreateEvent) {
      itemsMceClient.push({
        title: translate('mce:navigation_title_resource'),
        path: PATH_PAGE.resource.mce.root,
        icon: 'campaign',
        id: 'events-nav-client'
      });
    }
    if (canManageEvents && !user.userResourceId) {
      itemsMceClient.push({
        title: translate('mce:navigation_events_title'),
        path: PATH_PAGE.manager.mce.events,
        icon: 'form_edit',
        id: 'mce-events-nav'
      });
    }
    if (canModifyForm) {
      itemsMceClient.push({
        title: translate('mce:mce_forms'),
        path: PATH_PAGE.manager.mce.root,
        icon: 'form_edit',
        id: 'mce-forms-nav'
      });
    }

    const clientGroups = [{ subheader: 'Espace client', items: itemsClient }];
    if (itemsMceClient.length > 0 && !isMobile) {
      clientGroups.push({ subheader: 'MCE', items: itemsMceClient });
    }
    if (user.is_admin_link) {
      clientGroups.push({ subheader: 'Administration', items: itemsAdmin });
    }

    // On Time manager — nav spécifique quand on est sur le clocking app
    if (window.env.isClockingApp && user.role === 'manager' && sessionStorage.getItem('ontime_email_login')) {
      const itemsOnTimeManager = [
        {
          title: 'Tableau de bord',
          path: PATH_AUTH.clockingApp.managerHome,
          icon: 'dashboard',
          id: 'ontime-manager-dashboard-nav'
        },
        { title: 'Planning', path: '/pointages/manager-planning', icon: 'planning', id: 'ontime-manager-planning-nav' },
        ...(isMobile ? [{ title: '', path: '', icon: '', id: 'ontime-fab-placeholder', isFab: true }] : []),
        { title: 'Pointages', path: '/pointages/my-clockings', icon: 'clocking', id: 'ontime-manager-clockings-nav' },
        { title: 'Main courante', path: '/pointages/mce/manager/events', icon: 'mce', id: 'ontime-manager-mce-nav' }
      ];
      return [{ subheader: 'Espace manager', items: itemsOnTimeManager }];
    }

    // On Time agent — nav spécifique quand on est sur le clocking app
    if (window.env.isClockingApp && user.role === 'resource') {
      const planningMenuItems = [
        { title: translate('common:planning_menu.my_planning'), path: '/pointages/planning', icon: 'calendar_today' },
        { title: translate('common:planning_menu.site_planning'), path: '/pointages/site-planning', icon: 'event_note' },
      ];
      const itemsOnTimeResource = [
        { title: 'Tableau de bord', path: PATH_AUTH.clockingApp.resourceHome, icon: 'dashboard', id: 'ontime-dashboard-nav' },
        { title: 'Planning', path: '/pointages/planning', icon: 'planning', id: 'ontime-planning-nav', menuItems: planningMenuItems },
        ...(isMobile ? [{ title: '', path: '', icon: '', id: 'ontime-fab-placeholder', isFab: true }] : []),
        { title: 'Pointages', path: '/pointages/my-clockings', icon: 'clocking', id: 'ontime-clockings-nav' },
        { title: 'Main courante', path: '/pointages/mce/manager/events', icon: 'mce', id: 'ontime-mce-nav' }
      ];
      return [{ subheader: 'Comète On Time', items: itemsOnTimeResource }];
    }

    // On Time client — même nav que l'agent, avec les paths client
    if (window.env.isClockingApp && user.role === 'client') {
      const itemsOnTimeClient = [
        {
          title: 'Tableau de bord',
          path: PATH_AUTH.clockingApp.clientHome,
          icon: 'dashboard',
          id: 'ontime-client-dashboard-nav'
        },
        {
          title: 'Planning',
          path: PATH_AUTH.clockingApp.clientPlanning,
          icon: 'planning',
          id: 'ontime-client-planning-nav'
        },
        ...(isMobile ? [{ title: '', path: '', icon: '', id: 'ontime-fab-placeholder', isFab: true }] : []),
        { title: 'Pointages', path: '/pointages/my-clockings', icon: 'clocking', id: 'ontime-client-clockings-nav' },
        { title: 'Main courante', path: '/pointages/mce/manager/events', icon: 'mce', id: 'ontime-client-mce-nav' }
      ];
      return [{ subheader: 'Espace client', items: itemsOnTimeClient }];
    }

    const items = {
      manager: managerGroups,
      resource: resourceGroups,
      client: clientGroups,
      manager_partner: [
        {
          subheader: 'Espace partenaire',
          items: itemsPartner
        }
      ]
    };

    return items[user.role];
  }
};
